/*
 * @Author: Yumeng Xue
 * @Date: 2024-01-07 14:20:41
 * @LastEditTime: 2026-02-22 22:36:58
 * @LastEditors: Yumeng Xue
 * @Description: Canvas component with WebGPU rendering - Modern UI Redesign
 * @FilePath: /topo-mess/src/components/Canvas.tsx
 */
import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { AlertTriangle, Info, MousePointer2, Layers, Timer, Square } from 'lucide-react';
import { LineData, Binning } from '../core/binning';
import { StructureTensorScanner } from '../core/structure-tensor-scanner';
import { WebGPUMessinessRenderer, HeatmapMode } from '../core/webgpu-renderer';
import { analyzeSelectedLines, SelectedLineAnalysisParams } from '../core/selected-line-analysis';
import { MSTResult } from '../core/analysis-engine';
import { PAPER_UI_TERMS } from '../constants/paperTerminology';
import { Cluster } from '../types/cluster';
import { LoadingOverlay, Badge } from './ui';
import { cn } from '../lib/utils';
import { ColormapName } from '../core/colormaps';

interface CanvasProps {
    lines: LineData[];
    futureWindowW: number;
    binResolution: number;
    heatmapMode: HeatmapMode;
    lineOpacity: number;
    queryResultOpacity: number;
    queryAnalysisBinResolution?: number;
    width: number;
    height: number;
    useHistogramEqualization: boolean;
    useGlobalDensityScale?: boolean;
    selectedColormap: ColormapName;
    selectedClusterId?: string | null;
    clusters?: Cluster[];
    showStructureTensors?: boolean;
    tensorSamplingRate?: number;
    onAnalysisComplete?: (results: {
        mstResult: MSTResult | null;
        lineIds: number[];
    }) => void;
    onQueryAnalysisStateChange?: (isAnalyzing: boolean) => void;
    onLineFinalScoresUpdate?: (scores: Float32Array | null) => void;
    focusContextParams?: {
        threshold: number;
        saturationDrop: number;
        transition: number;
        hatchDensity: number;
        hatchThickness: number;
        hatchOpacity: number;
    };
}

type SelectedLineAnalysisWorkerResponse =
    | { type: 'result'; requestId: number; lineImportances?: Float32Array | number[] }
    | { type: 'error'; requestId: number; error: string };

export interface CanvasHandle {
    setVisibleLines: (lineIds: number[] | null) => void;
    highlightSingleLine: (lineId: number | null) => void;
    clearHighlight: () => void;
    renderThumbnail: (lineIds: number[], width?: number, height?: number) => Promise<string>;
    setActiveLineIds: (activeIds: Set<number>) => Promise<void>;
    getActiveLineIds: () => number[];
    getAllLineIds: () => number[];
    updateActiveLines: (activeLineIds: number[]) => Promise<void>;
    downloadCanvas: () => Promise<void>;
}

// Simple toast notification
const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    // Create a simple toast element
    const toast = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500';
    toast.className = `fixed bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg text-white text-sm font-medium shadow-lg z-[100] animate-fade-in ${bgColor}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transition = 'opacity 0.3s';
        setTimeout(() => toast.remove(), 300);
    }, 2500);
};

const SELECTION_ANALYSIS_CANCELLED = 'Selected line analysis cancelled';

type QueryWindowRect = { x: number; y: number; width: number; height: number };

const intersectLineIdSets = (lineIdSets: number[][]): number[] => {
    if (lineIdSets.length === 0) {
        return [];
    }

    const [firstSet, ...remainingSets] = lineIdSets.map(lineIds => new Set(lineIds));
    return Array.from(firstSet)
        .filter(lineId => remainingSets.every(lineIdSet => lineIdSet.has(lineId)))
        .sort((a, b) => a - b);
};

const Canvas = forwardRef<CanvasHandle, CanvasProps>(({
    lines,
    futureWindowW,
    binResolution,
    heatmapMode,
    lineOpacity,
    queryResultOpacity,
    queryAnalysisBinResolution,
    width,
    height,
    useGlobalDensityScale = true,
    selectedColormap,
    selectedClusterId,
    clusters = [], showStructureTensors = false,
    tensorSamplingRate = 5, onAnalysisComplete,
    onQueryAnalysisStateChange,
    onLineFinalScoresUpdate,
    focusContextParams,
    useHistogramEqualization
}, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const secondaryCanvasRef = useRef<HTMLCanvasElement>(null);
    const renderer = useRef<WebGPUMessinessRenderer | null>(null);
    const secondaryRenderer = useRef<WebGPUMessinessRenderer | null>(null);
    const binning = useRef<Binning | null>(null);
    const analysisResults = useRef<{ lineIds: number[] }>({ lineIds: [] });
    const scanner = useRef<StructureTensorScanner | null>(null);
    const fullDatasetScanner = useRef<StructureTensorScanner | null>(null);
    const selectionAnalysisWorker = useRef<Worker | null>(null);
    const selectionAnalysisWorkerInitialized = useRef(false);
    const selectionAnalysisRunId = useRef(0);
    const selectionAnalysisReject = useRef<((error: Error) => void) | null>(null);
    const queryAnalysisStateRunId = useRef(0);
    const queryResultLineIdsRef = useRef<number[] | null>(null);
    const focusContextParamsRef = useRef(focusContextParams);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [webGPUSupported, setWebGPUSupported] = useState(true);
    const [rendererInfo, setRendererInfo] = useState<string>("");
    const [showUIOverlays, setShowUIOverlays] = useState(true);

    // Selection interaction state
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
    const [selectionEnd, setSelectionEnd] = useState<{ x: number; y: number } | null>(null);
    const [queryWindowRects, setQueryWindowRects] = useState<QueryWindowRect[]>([]);
    const [intersectionQueryEnabled, setIntersectionQueryEnabled] = useState(false);

    // Pan interaction state
    const [isPanMode, setIsPanMode] = useState(false);
    const [isPanning, setIsPanning] = useState(false);
    const panStartRef = useRef<{ x: number; y: number; viewportX: number; viewportY: number } | null>(null);

    // Selection mode: 'direct' = select all intersecting lines, 'timebox' = TimeBox (left-in, right-out only)
    const [selectionMode, setSelectionMode] = useState<'direct' | 'timebox'>('direct');
    const hasTemporalTrajectories = lines.some(line => Array.isArray(line.times) && line.times.length > 0);
    const canUseTimeBox = !hasTemporalTrajectories;
    const timeBoxEnabled = !hasTemporalTrajectories && selectionMode === 'timebox';

    // Overlay canvas for structure tensors
    const overlayCanvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (hasTemporalTrajectories && selectionMode === 'timebox') {
            setSelectionMode('direct');
        }
    }, [hasTemporalTrajectories, selectionMode]);

    useEffect(() => {
        focusContextParamsRef.current = focusContextParams;
    }, [focusContextParams]);

    const terminateSelectionAnalysisWorker = useCallback((disposeWorker: boolean = false) => {
        selectionAnalysisRunId.current += 1;
        const hasPendingAnalysis = selectionAnalysisReject.current !== null;
        if (selectionAnalysisReject.current) {
            selectionAnalysisReject.current(new Error(SELECTION_ANALYSIS_CANCELLED));
            selectionAnalysisReject.current = null;
        }
        if ((disposeWorker || hasPendingAnalysis) && selectionAnalysisWorker.current) {
            selectionAnalysisWorker.current.terminate();
            selectionAnalysisWorker.current = null;
            selectionAnalysisWorkerInitialized.current = false;
        }
    }, []);

    const runSelectedLineAnalysis = useCallback(async (
        selectedLineIds: number[]
    ): Promise<Float32Array> => {
        const buildSelectedLineAnalysisParams = (): SelectedLineAnalysisParams => ({
            lines: selectedLineIds
                .map((lineId) => lines[lineId])
                .filter((line): line is LineData => Boolean(line)),
            width,
            height,
            binResolution: queryAnalysisBinResolution ?? binResolution,
        });

        if (typeof Worker === 'undefined') {
            return analyzeSelectedLines(buildSelectedLineAnalysisParams()).lineImportances;
        }

        try {
            terminateSelectionAnalysisWorker();

            const runId = selectionAnalysisRunId.current;
            let worker = selectionAnalysisWorker.current;

            if (!worker) {
                worker = new Worker(
                    new URL('../workers/selectedLineAnalysis.worker.ts', import.meta.url)
                );
                selectionAnalysisWorker.current = worker;
            }

            if (!selectionAnalysisWorkerInitialized.current) {
                worker.postMessage({
                    type: 'init',
                    lines,
                });
                selectionAnalysisWorkerInitialized.current = true;
            }

            return await new Promise<Float32Array>((resolve, reject) => {
                selectionAnalysisReject.current = reject;

                worker!.onmessage = (event: MessageEvent<SelectedLineAnalysisWorkerResponse>) => {
                    if (runId !== selectionAnalysisRunId.current) {
                        return;
                    }

                    selectionAnalysisReject.current = null;

                    if (event.data?.type === 'error') {
                        if (selectionAnalysisWorker.current === worker) {
                            worker!.terminate();
                            selectionAnalysisWorker.current = null;
                            selectionAnalysisWorkerInitialized.current = false;
                        }
                        reject(new Error(event.data.error));
                        return;
                    }

                    const lineImportances = event.data?.lineImportances;
                    resolve(
                        lineImportances instanceof Float32Array
                            ? lineImportances
                            : Float32Array.from(lineImportances ?? [])
                    );
                };

                worker!.onerror = (event) => {
                    if (runId !== selectionAnalysisRunId.current) {
                        return;
                    }

                    selectionAnalysisReject.current = null;
                    if (selectionAnalysisWorker.current === worker) {
                        worker!.terminate();
                        selectionAnalysisWorker.current = null;
                        selectionAnalysisWorkerInitialized.current = false;
                    }
                    reject(new Error(event.message || 'Selected line analysis worker failed'));
                };

                worker!.postMessage({
                    type: 'analyze',
                    requestId: runId,
                    lineIds: selectedLineIds,
                    width,
                    height,
                    binResolution: queryAnalysisBinResolution ?? binResolution,
                });
            });
        } catch (error) {
            console.warn('Falling back to main-thread selected-line analysis:', error);
            return analyzeSelectedLines(buildSelectedLineAnalysisParams()).lineImportances;
        }
    }, [binResolution, height, lines, queryAnalysisBinResolution, terminateSelectionAnalysisWorker, width]);

    const updateQueryResultHighlight = useCallback((lineIds: number[] | null) => {
        queryResultLineIdsRef.current = lineIds;

        if (!renderer.current) return;

        if (lineIds === null) {
            renderer.current.clearHighlight();
            return;
        }

        renderer.current.highlightLines(lineIds, true, queryResultOpacity);
    }, [queryResultOpacity]);

    const resetQueryResultState = useCallback((clearSelectionBox: boolean = false) => {
        terminateSelectionAnalysisWorker();
        queryAnalysisStateRunId.current += 1;
        analysisResults.current.lineIds = [];
        queryResultLineIdsRef.current = null;

        if (renderer.current) {
            renderer.current.clearHighlight();
            if (clearSelectionBox) {
                renderer.current.drawSelectionBox(null);
            }
        }

        if (clearSelectionBox) {
            setQueryWindowRects([]);
        }

        onLineFinalScoresUpdate?.(null);
        onQueryAnalysisStateChange?.(false);
        onAnalysisComplete?.({
            mstResult: null,
            lineIds: []
        });
    }, [onAnalysisComplete, onLineFinalScoresUpdate, onQueryAnalysisStateChange, terminateSelectionAnalysisWorker]);

    const clearQueryResultOnly = useCallback(() => {
        queryAnalysisStateRunId.current += 1;
        analysisResults.current.lineIds = [];
        queryResultLineIdsRef.current = null;

        if (renderer.current) {
            renderer.current.clearHighlight();
        }

        onLineFinalScoresUpdate?.(null);
        onQueryAnalysisStateChange?.(false);
        onAnalysisComplete?.({
            mstResult: null,
            lineIds: []
        });
    }, [onAnalysisComplete, onLineFinalScoresUpdate, onQueryAnalysisStateChange]);

    const commitQueryResult = useCallback(async (
        selectedLines: number[],
        label: string = PAPER_UI_TERMS.query.queryResult,
        options: { deferDisplayUntilScores?: boolean } = {}
    ) => {
        const deferDisplayUntilScores = Boolean(options.deferDisplayUntilScores);

        const publishUnscoredResult = () => {
            updateQueryResultHighlight(selectedLines);
            analysisResults.current.lineIds = selectedLines;
            onLineFinalScoresUpdate?.(null);
            onAnalysisComplete?.({
                mstResult: null,
                lineIds: selectedLines
            });
        };

        if (selectedLines.length < 3) {
            publishUnscoredResult();
            showToast(`${label}: ${selectedLines.length} lines`, 'success');
            queryAnalysisStateRunId.current += 1;
            onQueryAnalysisStateChange?.(false);
            showToast('Query result needs at least 3 lines', 'info');
            return;
        }

        if (!deferDisplayUntilScores) {
            publishUnscoredResult();
            showToast(`${label}: ${selectedLines.length} lines`, 'success');
        }

        const analysisStateRunId = queryAnalysisStateRunId.current + 1;
        queryAnalysisStateRunId.current = analysisStateRunId;

        try {
            onQueryAnalysisStateChange?.(true);
            const lineImportances = await runSelectedLineAnalysis(selectedLines);

            if (analysisStateRunId !== queryAnalysisStateRunId.current) {
                return;
            }

            const globalLineFinalScores = new Float32Array(lines.length).fill(0);
            selectedLines.forEach((lineId, idx) => {
                globalLineFinalScores[lineId] = lineImportances[idx];
            });

            analysisResults.current.lineIds = selectedLines;
            onLineFinalScoresUpdate?.(globalLineFinalScores);
            if (deferDisplayUntilScores) {
                onAnalysisComplete?.({
                    mstResult: null,
                    lineIds: selectedLines
                });
                showToast(`${label}: ${selectedLines.length} lines`, 'success');
            }
        } catch (error) {
            if ((error as Error).message === SELECTION_ANALYSIS_CANCELLED) {
                return;
            }

            console.error('Failed to compute line importances:', error);
            showToast(PAPER_UI_TERMS.status.queryScoringFailed, 'error');
        } finally {
            if (analysisStateRunId === queryAnalysisStateRunId.current) {
                onQueryAnalysisStateChange?.(false);
            }
        }
    }, [
        lines.length,
        onAnalysisComplete,
        onLineFinalScoresUpdate,
        onQueryAnalysisStateChange,
        runSelectedLineAnalysis,
        updateQueryResultHighlight
    ]);

    /**
     * 绘制结构张量：使用椭圆可视化每个bin的方向信息
     * 从Bin的Structure Tensor (Jxx, Jxy, Jyy)计算主方向和次方向
     */
    const drawStructureTensors = useCallback((sampling: number = 5) => {
        if (!overlayCanvasRef.current || !binning.current) return;

        const canvas = overlayCanvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // 获取设备像素比，与pixi.js保持一致
        const dpr = window.devicePixelRatio || 1;

        // 设置canvas的实际像素尺寸
        const displayWidth = canvas.clientWidth;
        const displayHeight = canvas.clientHeight;

        // 调试信息
        console.log('[Structure Tensor] DPR:', dpr, 'Display:', displayWidth, 'x', displayHeight, 'Canvas:', canvas.width, 'x', canvas.height);

        if (canvas.width !== displayWidth * dpr || canvas.height !== displayHeight * dpr) {
            canvas.width = displayWidth * dpr;
            canvas.height = displayHeight * dpr;
            console.log('[Structure Tensor] Resized canvas to:', canvas.width, 'x', canvas.height);
        }

        // 清空画布
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!showStructureTensors) return;

        // 缩放坐标系统以匹配设备像素比
        ctx.save();
        ctx.scale(dpr, dpr);

        const bins = binning.current.getBins();
        const [binWidth, binHeight] = binning.current.getBinSize();
        const [xBinCount, yBinCount] = binning.current.getBinCounts();

        console.log('[Structure Tensor] Bin size:', binWidth, 'x', binHeight, 'Count:', xBinCount, 'x', yBinCount, 'Sampling:', sampling);

        // 获取世界坐标系统的高度用于y轴翻转
        const worldHeight = binning.current.height;

        let drawnCount = 0;

        // 采样绘制以避免过度绘制
        for (let x = 0; x < xBinCount; x += sampling) {
            for (let y = 0; y < yBinCount; y += sampling) {
                const bin = bins[x][y];

                // 跳过没有线条通过的bin
                if (bin.lineIds.length === 0) continue;

                // 聚合所有线条的张量
                let totalJxx = 0, totalJxy = 0, totalJyy = 0;
                for (let i = 0; i < bin.lineIds.length; i++) {
                    totalJxx += bin.Jxx[i];
                    totalJxy += bin.Jxy[i];
                    totalJyy += bin.Jyy[i];
                }

                // 计算特征值和特征向量
                // J = [Jxx, Jxy]
                //     [Jxy, Jyy]
                // 特征值: λ = (trace ± sqrt(trace² - 4*det)) / 2
                const trace = totalJxx + totalJyy;
                const det = totalJxx * totalJyy - totalJxy * totalJxy;
                const discriminant = Math.sqrt(Math.max(0, trace * trace - 4 * det));

                const lambda1 = (trace + discriminant) / 2; // 主特征值(大)
                const lambda2 = (trace - discriminant) / 2; // 次特征值(小)

                // 跳过退化情况
                if (lambda1 < 1e-6) continue;

                // 主方向对应lambda1的特征向量
                // (Jxx - λ)vx + Jxy*vy = 0
                let angle = 0;
                if (Math.abs(totalJxy) > 1e-6) {
                    angle = Math.atan2(lambda1 - totalJxx, totalJxy);
                } else if (totalJxx > totalJyy) {
                    angle = 0; // 水平
                } else {
                    angle = Math.PI / 2; // 垂直
                }

                // Bin中心位置
                const centerX = (x + 0.5) * binWidth;
                const centerY = (y + 0.5) * binHeight;

                // 翻转y坐标以匹配pixi.js坐标系统（y轴向下）
                const flippedY = worldHeight - centerY;

                // 椭圆半轴长度(根据特征值缩放)
                // 使用特征值的实际比例来决定椭圆形状
                const baseSize = Math.min(binWidth, binHeight) * 0.8 * sampling;

                // 计算各向异性度（anisotropy）：lambda2/lambda1的比值
                // 当比值接近1时，方向性弱（圆形）；比值接近0时，方向性强（扁椭圆）
                const anisotropy = lambda2 / lambda1;

                // 长轴：基于张量强度（lambda1）缩放，使用对数以避免极端值
                const tensorStrength = Math.log(1 + lambda1) / Math.log(1 + lambda1 * 10);
                const majorAxis = baseSize * (0.6 + tensorStrength * 0.4);

                // 短轴：基于各向异性度，anisotropy越小，椭圆越扁
                const minorAxis = majorAxis * (0.2 + anisotropy * 0.8);

                // 调试第一个椭圆
                if (drawnCount === 0) {
                    console.log('[Structure Tensor] First ellipse - Lambda1:', lambda1.toFixed(4), 'Lambda2:', lambda2.toFixed(4), 'Anisotropy:', anisotropy.toFixed(4));
                    console.log('[Structure Tensor] Position - X:', centerX, 'Y:', centerY, 'Flipped Y:', flippedY);
                    console.log('[Structure Tensor] Size - Major:', majorAxis.toFixed(2), 'Minor:', minorAxis.toFixed(2), 'Ratio:', (minorAxis / majorAxis).toFixed(3), 'Angle:', (angle * 180 / Math.PI).toFixed(1), '°');
                }
                drawnCount++;

                // 绘制椭圆
                ctx.save();
                ctx.translate(centerX, flippedY);
                ctx.rotate(angle);

                // 椭圆样式 - 空心椭圆，更明显的边框
                ctx.strokeStyle = 'rgba(255, 80, 80, 0.9)';
                ctx.lineWidth = 2.5;

                ctx.beginPath();
                ctx.ellipse(0, 0, majorAxis, minorAxis, 0, 0, 2 * Math.PI);
                ctx.stroke();

                ctx.restore();
            }
        }

        console.log('[Structure Tensor] Total drawn:', drawnCount, 'ellipses');

        // 恢复坐标系统缩放
        ctx.restore();
    }, [showStructureTensors]);

    // Keyboard shortcuts: H to toggle UI overlays, Space for pan mode
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // H key: Toggle UI overlays
            if (event.key.toLowerCase() === 'h' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                setShowUIOverlays(prev => !prev);
            }
            // Space key: Enable pan mode
            if (event.code === 'Space' && !isPanMode && !event.ctrlKey && !event.metaKey && !event.altKey) {
                event.preventDefault(); // Prevent page scroll
                setIsPanMode(true);
            }
        };

        const handleKeyUp = (event: KeyboardEvent) => {
            // Space key: Disable pan mode
            if (event.code === 'Space') {
                setIsPanMode(false);
                setIsPanning(false);
                panStartRef.current = null;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [isPanMode]);

    // Check WebGPU support
    useEffect(() => {
        if (!navigator.gpu) {
            setWebGPUSupported(false);
            showToast('WebGPU not supported. Please use Chrome 113+, Edge 113+, or Safari 18+', 'error');
        }
    }, []);

    // Prevent Safari gesture events (gesturestart/change/end) from triggering browser zoom on the canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const preventGesture = (e: Event) => { e.preventDefault(); };
        canvas.addEventListener('gesturestart', preventGesture, { passive: false } as any);
        canvas.addEventListener('gesturechange', preventGesture, { passive: false } as any);
        canvas.addEventListener('gestureend', preventGesture, { passive: false } as any);

        return () => {
            canvas.removeEventListener('gesturestart', preventGesture);
            canvas.removeEventListener('gesturechange', preventGesture);
            canvas.removeEventListener('gestureend', preventGesture);
        };
    }, []);

    useEffect(() => {
        return () => {
            terminateSelectionAnalysisWorker(true);
        };
    }, [terminateSelectionAnalysisWorker]);

    useEffect(() => {
        terminateSelectionAnalysisWorker(true);
        setQueryWindowRects([]);
        setIntersectionQueryEnabled(false);
        setSelectionMode(lines.length > 0 && canUseTimeBox ? 'timebox' : 'direct');
    }, [canUseTimeBox, lines, terminateSelectionAnalysisWorker]);

    // --- Effect A: Initialize Renderer ---
    // Renderer 初始化仅跟随数据和画布几何变化；其它显示参数由后续 effect 单独同步。
    useEffect(() => {
        if (!canvasRef.current || lines.length === 0 || !webGPUSupported) return;

        let cancelled = false;

        if (renderer.current) {
            renderer.current.destroy();
            renderer.current = null;
        }
        scanner.current = null;
        fullDatasetScanner.current = null;
        binning.current = null;

        setLoadingText(PAPER_UI_TERMS.status.loadingMainView);
        setIsLoading(true);

        (async () => {
            try {
                setLoadingText(PAPER_UI_TERMS.status.preparingBins);
                const binningInstance = new Binning(width, height, binResolution, binResolution);
                binningInstance.executeBinning(lines, [[1]]);
                if (cancelled) return;
                binning.current = binningInstance;

                setLoadingText(PAPER_UI_TERMS.status.initializingFieldScan);
                const scannerInstance = new StructureTensorScanner(
                    binning.current.getBinCounts()[0],
                    binning.current.getBinCounts()[1]
                );
                if (cancelled) return;
                scanner.current = scannerInstance;
                fullDatasetScanner.current = scannerInstance;

                setLoadingText(PAPER_UI_TERMS.status.initializingRenderer);
                const rendererInstance = await WebGPUMessinessRenderer.create(
                    canvasRef.current!,
                    lines,
                    binningInstance
                );
                if (cancelled) {
                    rendererInstance.destroy();
                    return;
                }
                renderer.current = rendererInstance;

                const info = await rendererInstance.getRendererInfo();
                if (!cancelled) {
                    setRendererInfo(`Renderer: ${info.type}${info.gpu ? ` | GPU: ${info.gpu}` : ''}`);
                }

                setLoadingText(PAPER_UI_TERMS.status.computingField);
                scannerInstance.initField(binningInstance, lines, 1, true);
                const scanResult = scannerInstance.query(futureWindowW);
                await rendererInstance.updateHeatmap(scanResult, heatmapMode, {
                    useHistogramEqualization,
                    normalizationOptions: useGlobalDensityScale
                        ? { densityReference: scanResult }
                        : undefined
                });

                if (!cancelled && onLineFinalScoresUpdate) onLineFinalScoresUpdate(null);

                rendererInstance.setContextLinesAlpha(lineOpacity);

                if (!cancelled) {
                    setIsLoading(false);
                    setLoadingText("");
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("WebGPU Renderer initialization error:", error);
                    showToast(`${PAPER_UI_TERMS.status.rendererInitFailed}: ${(error as Error).message}`, 'error');
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            if (renderer.current) {
                renderer.current.destroy();
                renderer.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines, binResolution, width, height, webGPUSupported]);

    const getDensityNormalizationOptions = useCallback(() => {
        if (!useGlobalDensityScale || !fullDatasetScanner.current) {
            return undefined;
        }

        return {
            densityReference: fullDatasetScanner.current.query(futureWindowW)
        };
    }, [futureWindowW, useGlobalDensityScale]);

    // --- Effect B: Update TDA Scan ---
    useEffect(() => {
        if (!renderer.current || !binning.current || !scanner.current || lines.length === 0) {
            return;
        }

        if (heatmapMode === "Off") {
            renderer.current.setHeatmapVisible(false);
            return;
        } else {
            renderer.current.setHeatmapVisible(true);
        }

        if (heatmapMode === "IlluminatedMessiness" && focusContextParamsRef.current) {
            renderer.current.setFocusContextParams(focusContextParamsRef.current);
        }

        setIsLoading(true);
        setLoadingText(
            heatmapMode === "IlluminatedMessiness"
                ? PAPER_UI_TERMS.status.updatingFocusContextView
                : PAPER_UI_TERMS.status.refreshingFieldView
        );

        const debounceDelay = heatmapMode === "IlluminatedMessiness" ? 16 : 0;
        const timer = setTimeout(async () => {
            const scanResult = scanner.current!.query(futureWindowW);
            if (onLineFinalScoresUpdate) {
                onLineFinalScoresUpdate(null);
            }
            await renderer.current!.updateHeatmap(scanResult, heatmapMode, {
                useHistogramEqualization,
                normalizationOptions: getDensityNormalizationOptions()
            });
            setIsLoading(false);
        }, debounceDelay);

        return () => clearTimeout(timer);
    }, [futureWindowW, heatmapMode, lines, onLineFinalScoresUpdate, selectedColormap, useHistogramEqualization, getDensityNormalizationOptions]);

    // --- Effect B1: Reapply Focus+Context View Parameters Without Recomputing Fields ---
    useEffect(() => {
        if (!renderer.current || heatmapMode !== "IlluminatedMessiness" || !focusContextParams) {
            return;
        }

        renderer.current.setFocusContextParams(focusContextParams);
        renderer.current.reapplyFocusContextFilter();
    }, [focusContextParams, heatmapMode]);

    // --- Effect C: Update Line Opacity ---
    useEffect(() => {
        if (renderer.current) {
            renderer.current.setContextLinesAlpha(lineOpacity);
        }
    }, [lineOpacity]);

    // --- Effect C1: Update Query Result Opacity ---
    useEffect(() => {
        if (!renderer.current || queryResultLineIdsRef.current === null) {
            return;
        }

        renderer.current.highlightLines(queryResultLineIdsRef.current, true, queryResultOpacity);
    }, [queryResultOpacity]);

    // --- Effect C2: Draw Structure Tensors ---
    useEffect(() => {
        if (binning.current) {
            // 使用requestAnimationFrame确保DOM已更新
            requestAnimationFrame(() => {
                drawStructureTensors(tensorSamplingRate);
            });
        }
    }, [showStructureTensors, tensorSamplingRate, lines, binResolution, width, height, drawStructureTensors]);

    // --- Effect D: Render Selected Cluster in Secondary Canvas ---
    useEffect(() => {
        if (!secondaryCanvasRef.current || !selectedClusterId || clusters.length === 0) {
            if (secondaryRenderer.current) {
                secondaryRenderer.current.destroy();
                secondaryRenderer.current = null;
            }
            return;
        }

        const selectedCluster = clusters.find(c => c.id === selectedClusterId);
        if (!selectedCluster) return;

        (async () => {
            try {
                const clusterLines = selectedCluster.lineIds
                    .filter(id => id < lines.length)
                    .map(id => lines[id]);

                if (clusterLines.length === 0) return;

                const secondaryBinning = new Binning(
                    width,
                    height,
                    binResolution,
                    binResolution
                );
                secondaryBinning.executeBinning(clusterLines, [[1]]);

                if (secondaryRenderer.current) {
                    secondaryRenderer.current.destroy();
                }

                const secondaryRendererInstance = await WebGPUMessinessRenderer.create(
                    secondaryCanvasRef.current!,
                    clusterLines,
                    secondaryBinning
                );

                secondaryRenderer.current = secondaryRendererInstance;

                secondaryRendererInstance.setHeatmapVisible(false);
                secondaryRendererInstance.setContextLinesAlpha(1.0);

                showToast(`Displaying ${PAPER_UI_TERMS.subset.subset.toLowerCase()}: ${selectedCluster.count} lines`, 'success');
            } catch (error) {
                console.error('Failed to render secondary canvas:', error);
                showToast(`Failed to display ${PAPER_UI_TERMS.subset.subset.toLowerCase()}`, 'error');
            }
        })();

        return () => {
            if (secondaryRenderer.current) {
                secondaryRenderer.current.destroy();
                secondaryRenderer.current = null;
            }
        };
    }, [selectedClusterId, clusters, lines, width, height, binResolution]);

    // --- Selection interaction handlers ---
    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!renderer.current) return;

        terminateSelectionAnalysisWorker();

        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;

        const screenX = e.clientX - rect.left;
        const screenY = e.clientY - rect.top;

        const viewport = renderer.current.getViewport();

        // Pan mode: Space + Left click
        if (isPanMode && e.button === 0) {
            setIsPanning(true);
            panStartRef.current = {
                x: screenX,
                y: screenY,
                viewportX: viewport.x,
                viewportY: viewport.y
            };
            return;
        }

        // Selection mode: Left click without Space
        if (e.button === 0) {
            const worldPos = viewport.toWorld(screenX, screenY);
            setIsSelecting(true);
            setSelectionStart({ x: worldPos.x, y: worldPos.y });
            setSelectionEnd({ x: worldPos.x, y: worldPos.y });
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!renderer.current) return;

        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;

        const screenX = e.clientX - rect.left;
        const screenY = e.clientY - rect.top;

        const viewport = renderer.current.getViewport();

        // Handle panning
        if (isPanning && panStartRef.current) {
            const dx = screenX - panStartRef.current.x;
            const dy = screenY - panStartRef.current.y;

            // Apply pan to viewport
            viewport.x = panStartRef.current.viewportX + dx;
            viewport.y = panStartRef.current.viewportY + dy;
            return;
        }

        // Handle selection
        if (isSelecting && selectionStart) {
            const worldPos = viewport.toWorld(screenX, screenY);
            setSelectionEnd({ x: worldPos.x, y: worldPos.y });

            const selectionRect = {
                x: Math.min(selectionStart.x, worldPos.x),
                y: Math.min(selectionStart.y, worldPos.y),
                width: Math.abs(worldPos.x - selectionStart.x),
                height: Math.abs(worldPos.y - selectionStart.y)
            };

            if (intersectionQueryEnabled) {
                renderer.current.drawSelectionBoxes([...queryWindowRects, selectionRect]);
            } else {
                renderer.current.drawSelectionBox(selectionRect);
            }
        }
    };

    const handleMouseUp = async (e: React.MouseEvent<HTMLCanvasElement>) => {
        // Handle pan mode
        if (isPanning) {
            setIsPanning(false);
            panStartRef.current = null;
            return;
        }

        // Handle selection mode
        if (!isSelecting || !renderer.current || !selectionStart || !selectionEnd) {
            setIsSelecting(false);
            return;
        }

        const selectionRect = {
            x: Math.min(selectionStart.x, selectionEnd.x),
            y: Math.min(selectionStart.y, selectionEnd.y),
            width: Math.abs(selectionEnd.x - selectionStart.x),
            height: Math.abs(selectionEnd.y - selectionStart.y)
        };

        // 立即结束拖拽态，避免后续异步分析或 loading overlay 触发 mouseLeave
        // 把还在显示的 selection box 误清掉。
        setIsSelecting(false);
        setSelectionStart(null);
        setSelectionEnd(null);

        if (selectionRect.width < 2 || selectionRect.height < 2) {
            resetQueryResultState(true);
        } else {
            const isIntersectionQuery = intersectionQueryEnabled;
            const nextQueryWindowRects = isIntersectionQuery
                ? [...queryWindowRects, selectionRect]
                : [selectionRect];

            setQueryWindowRects(nextQueryWindowRects);

            if (isIntersectionQuery) {
                renderer.current.drawSelectionBoxes(nextQueryWindowRects);
            } else {
                renderer.current.drawSelectionBox(selectionRect);
            }

            const lineIdSets = nextQueryWindowRects.map(rect =>
                renderer.current!.extractLinesInRect(rect, timeBoxEnabled)
            );
            const selectedLines = isIntersectionQuery
                ? intersectLineIdSets(lineIdSets)
                : lineIdSets[0];

            if (selectedLines.length > 0) {
                const label = isIntersectionQuery && nextQueryWindowRects.length > 1
                    ? `Intersection Query (${nextQueryWindowRects.length} windows)`
                    : PAPER_UI_TERMS.query.queryResult;
                await commitQueryResult(selectedLines, label, {
                    deferDisplayUntilScores: isIntersectionQuery && nextQueryWindowRects.length > 1
                });
            } else if (isIntersectionQuery) {
                clearQueryResultOnly();
                showToast(`No lines found in the intersection of ${nextQueryWindowRects.length} windows`, 'info');
            } else {
                resetQueryResultState(true);
                showToast('No lines found in the query window', 'info');
            }
        }

    };

    const handleMouseLeave = () => {
        if (isSelecting && renderer.current) {
            if (intersectionQueryEnabled && queryWindowRects.length > 0) {
                renderer.current.drawSelectionBoxes(queryWindowRects);
            } else {
                renderer.current.drawSelectionBox(null);
            }
        }
        setIsSelecting(false);
        setSelectionStart(null);
        setSelectionEnd(null);
    };

    const handleSelectionModeChange = useCallback((mode: 'direct' | 'timebox') => {
        if (mode === 'timebox' && !canUseTimeBox) {
            showToast('This dataset is 2D trajectory data. Use Window Query.', 'info');
            return;
        }

        if (mode !== selectionMode) {
            resetQueryResultState(true);
        }

        setSelectionMode(mode);
    }, [canUseTimeBox, resetQueryResultState, selectionMode]);

    const handleIntersectionQueryToggle = useCallback(() => {
        const nextEnabled = !intersectionQueryEnabled;
        setIntersectionQueryEnabled(nextEnabled);

        if (!nextEnabled) {
            resetQueryResultState(true);
            showToast('Intersection Query disabled', 'info');
            return;
        }

        showToast('Intersection Query enabled. Draw multiple windows; ESC clears them.', 'info');
    }, [intersectionQueryEnabled, resetQueryResultState]);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
        setVisibleLines: (lineIds: number[] | null) => {
            updateQueryResultHighlight(lineIds);
        },
        highlightSingleLine: (lineId: number | null) => {
            if (!renderer.current) return;

            if (lineId === null) {
                renderer.current.clearFocus();
                return;
            }

            renderer.current.drawFocus([lineId]);
        },
        clearHighlight: () => {
            resetQueryResultState();
        },
        renderThumbnail: async (lineIds: number[], thumbWidth?: number, thumbHeight?: number): Promise<string> => {
            if (!renderer.current) {
                throw new Error('Renderer not initialized');
            }
            return await renderer.current.renderLinesToDataURL(lineIds, thumbWidth, thumbHeight);
        },
        setActiveLineIds: async (activeIds: Set<number>) => {
            if (renderer.current) {
                await renderer.current.setActiveLineIds(activeIds);
            }
        },
        getActiveLineIds: (): number[] => {
            if (renderer.current) {
                return renderer.current.getActiveLineIds();
            }
            return [];
        },
        getAllLineIds: (): number[] => {
            if (renderer.current) {
                return renderer.current.getAllLineIds();
            }
            return [];
        },
        downloadCanvas: async () => {
            if (!renderer.current) {
                showToast(PAPER_UI_TERMS.status.rendererNotReady, 'error');
                return;
            }

            try {
                showToast(PAPER_UI_TERMS.status.savingFigure, 'info');

                // 使用renderer的exportFullCanvas方法导出完整原始分辨率
                const dataUrl = await renderer.current.exportFullCanvas();

                if (!dataUrl) {
                    showToast(PAPER_UI_TERMS.status.imageGenerationFailed, 'error');
                    return;
                }

                // 从data URL创建下载链接
                const link = document.createElement('a');
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                link.download = `line-ensemble-view-${timestamp}.png`;
                link.href = dataUrl;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                showToast(PAPER_UI_TERMS.status.figureSaved, 'success');
            } catch (error) {
                console.error('Download error:', error);
                showToast(PAPER_UI_TERMS.status.figureSaveFailed, 'error');
            }
        },
        updateActiveLines: async (activeLineIds: number[]) => {
            if (!renderer.current || !binning.current || !scanner.current) {
                console.warn('Cannot update active lines: components not initialized');
                return;
            }

            try {
                setIsLoading(true);
                setLoadingText('Updating the remaining line ensemble...');

                const activeLineSet = new Set(activeLineIds);
                const activeLines = lines.filter((_, idx) => activeLineSet.has(idx));

                if (activeLines.length === 0) {
                    resetQueryResultState(true);
                    showToast(PAPER_UI_TERMS.status.noLinesRemaining, 'info');
                    setIsLoading(false);
                    return;
                }

                const newBinning = new Binning(width, height, binResolution, binResolution);
                newBinning.executeBinning(activeLines, [[1]]);
                binning.current = newBinning;

                const [binCountX, binCountY] = newBinning.getBinCounts();
                const newScanner = new StructureTensorScanner(binCountX, binCountY);
                newScanner.initField(newBinning, activeLines, 1);
                scanner.current = newScanner;

                renderer.current.setActiveLineIds(activeLineSet);

                const scanResult = newScanner.query(futureWindowW);
                await renderer.current.updateHeatmap(scanResult, heatmapMode, {
                    useHistogramEqualization,
                    normalizationOptions: getDensityNormalizationOptions()
                });

                resetQueryResultState();

                showToast(`${PAPER_UI_TERMS.status.ensembleUpdated}: ${activeLines.length} lines remaining`, 'success');
                setIsLoading(false);
            } catch (error) {
                console.error('Failed to update active lines:', error);
                showToast(PAPER_UI_TERMS.status.lineEnsembleViewUpdateFailed, 'error');
                setIsLoading(false);
            }
        }
    }));

    // Keyboard: Press Escape to clear selection
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && renderer.current) {
                resetQueryResultState(true);
                showToast(PAPER_UI_TERMS.query.queryCleared, 'info');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [resetQueryResultState]);

    return (
        <div className="relative flex flex-col w-full h-full overflow-hidden">
            {/* Main Canvas */}
            <div className={cn(
                "relative flex-1 transition-all duration-300",
                selectedClusterId && "h-1/2"
            )}>
                {/* WebGPU Not Supported Alert */}
                {!webGPUSupported && (
                    <div className="absolute inset-4 z-10">
                        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-amber-800">WebGPU Not Supported</h4>
                                <p className="text-sm text-amber-700 mt-1">
                                    Your browser doesn't support WebGPU. Please use Chrome 113+, Edge 113+, or Safari 18+
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Renderer Info Badge - Toggle with H key */}
                {rendererInfo && showUIOverlays && (
                    <div className="absolute top-3 left-3 z-10 transition-opacity duration-200">
                        <Badge variant="default" className="bg-black/70 text-white border-0 backdrop-blur-sm">
                            {rendererInfo}
                        </Badge>
                    </div>
                )}

                {/* Interaction Tips - Toggle with H key */}
                {lines.length > 0 && showUIOverlays && (
                    <div className="absolute top-3 right-3 z-10 transition-opacity duration-200">
                        <div className="flex flex-col gap-1 p-3 rounded-xl bg-black/70 backdrop-blur-sm text-white text-xs">
                            <div className="flex items-center gap-2 font-semibold mb-1">
                                <Info className="w-3 h-3" />
                                {PAPER_UI_TERMS.query.queryTips}
                            </div>

                            {/* Selection Mode Toggle */}
                            <div className="flex items-center gap-1 mb-1">
                                <button
                                    type="button"
                                    onClick={() => handleSelectionModeChange('direct')}
                                    className={cn(
                                        "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors",
                                        selectionMode === 'direct'
                                            ? "bg-blue-500 text-white"
                                            : "bg-white/10 text-white/60 hover:bg-white/20"
                                    )}
                                    title={PAPER_UI_TERMS.query.windowQueryTooltip}
                                >
                                    <Square className="w-3 h-3" />
                                    {PAPER_UI_TERMS.query.windowQuery}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleSelectionModeChange('timebox')}
                                    disabled={!canUseTimeBox}
                                    className={cn(
                                        "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors",
                                        selectionMode === 'timebox'
                                            ? "bg-amber-500 text-white"
                                            : "bg-white/10 text-white/60 hover:bg-white/20",
                                        !canUseTimeBox && "cursor-not-allowed bg-white/5 text-white/30 hover:bg-white/5"
                                    )}
                                    title={canUseTimeBox
                                        ? PAPER_UI_TERMS.query.timeBoxTooltip
                                        : 'This dataset is 2D trajectory data. Use Window Query.'}
                                >
                                    <Timer className="w-3 h-3" />
                                    {PAPER_UI_TERMS.query.timeBox}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleIntersectionQueryToggle}
                                    className={cn(
                                        "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors",
                                        intersectionQueryEnabled
                                            ? "bg-emerald-500 text-white"
                                            : "bg-white/10 text-white/60 hover:bg-white/20"
                                    )}
                                    title="Intersection Query: draw multiple windows and keep only lines found by every query window."
                                >
                                    <Layers className="w-3 h-3" />
                                    Intersect
                                </button>
                            </div>

                            {hasTemporalTrajectories && (
                                <div className="rounded bg-white/10 px-2 py-1 text-[10px] text-white/70">
                                    This dataset is 2D trajectory data. Use Window Query.
                                </div>
                            )}
                            {intersectionQueryEnabled && (
                                <div className="rounded bg-emerald-500/20 px-2 py-1 text-[10px] text-emerald-100">
                                    Intersection on: {queryWindowRects.length} window{queryWindowRects.length === 1 ? '' : 's'} placed.
                                </div>
                            )}

                            <div className="flex items-center gap-2">
                                <MousePointer2 className="w-3 h-3 opacity-60" />
                                <span>{PAPER_UI_TERMS.query.drawQueryWindow}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="opacity-60 text-[10px] font-mono px-1 rounded bg-white/10">SPACE</span>
                                <span className="opacity-60">+</span>
                                <MousePointer2 className="w-3 h-3 opacity-60" />
                                <span>{PAPER_UI_TERMS.query.panView}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="opacity-60 text-[10px]">🖱️</span>
                                <span>{PAPER_UI_TERMS.query.zoomView}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="opacity-60 text-[10px] font-mono px-1 rounded bg-white/10">ESC</span>
                                <span>{PAPER_UI_TERMS.query.clearQuery}</span>
                            </div>
                            <div className="border-t border-white/20 mt-2 pt-2">
                                <span className="text-[9px] opacity-50 italic">Press <kbd className="font-mono bg-white/10 px-1 rounded">H</kbd> to toggle overlays</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Show hint when overlays are hidden */}
                {!showUIOverlays && (
                    <div className="absolute bottom-3 left-3 z-10 text-[10px] text-white/50 font-mono pointer-events-none">
                        {PAPER_UI_TERMS.query.showOverlays}
                    </div>
                )}

                {/* Loading Overlay */}
                <LoadingOverlay visible={isLoading} label={loadingText} />

                {/* Canvas Element */}
                <div className="relative w-full h-full">
                    <canvas
                        ref={canvasRef}
                        width={width}
                        height={height}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        className={cn(
                            "block w-full h-full absolute inset-0",
                            isPanning ? "cursor-grabbing" :
                                isPanMode ? "cursor-grab" :
                                    isSelecting ? "cursor-crosshair" : "cursor-default"
                        )}
                    >
                        Your browser does not support Canvas.
                    </canvas>

                    {/* Overlay canvas for structure tensors */}
                    <canvas
                        ref={overlayCanvasRef}
                        className="block w-full h-full absolute inset-0 pointer-events-none"
                        style={{ zIndex: 10 }}
                    />
                </div>
            </div>

            {/* Secondary Canvas - Selected Cluster View */}
            {selectedClusterId && (
                <div className="relative h-1/2 border-t-2 border-primary-500 bg-surface-50">
                    {/* Header */}
                    <div className="absolute top-3 left-3 z-10">
                        <Badge variant="primary" className="gap-2 px-3 py-1.5">
                            <Layers className="w-3 h-3" />
                            {PAPER_UI_TERMS.panels.selectedSubsetView}
                        </Badge>
                    </div>

                    {/* Secondary Canvas */}
                    <canvas
                        ref={secondaryCanvasRef}
                        width={width}
                        height={height}
                        className="block w-full h-full bg-white"
                    >
                        Your browser does not support Canvas.
                    </canvas>
                </div>
            )}
        </div>
    );
});

Canvas.displayName = 'Canvas';

export default Canvas;
